(() => {
  'use strict';
  if (customElements.get('bm-dome-heading')) return;

  // Arc-length lookup makes gaps consistent even on an elliptical cap.
  function arcTable(rx, ry, halfAngle) {
    const points = [];
    let length = 0;
    for (let i = 0; i <= 160; i++) {
      const angle = -halfAngle + (2 * halfAngle * i / 160);
      const x = rx * Math.sin(angle);
      const y = ry * (1 - Math.cos(angle));
      if (i) length += Math.hypot(x - points[i - 1].x, y - points[i - 1].y);
      points.push({ angle, x, y, length });
    }
    return { points, length };
  }

  function atLength(table, distance) {
    const p = table.points;
    let i = 1;
    while (i < p.length - 1 && p[i].length < distance) i++;
    const a = p[i - 1], b = p[i];
    const t = Math.max(0, Math.min(1, (distance - a.length) / (b.length - a.length || 1)));
    return a.angle + (b.angle - a.angle) * t;
  }

  class BmDomeHeading extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._frame = 0;
      this._queue = () => {
        if (!this._frame) this._frame = requestAnimationFrame(() => {
          this._frame = 0;
          if (this.isConnected) this.measure();
        });
      };
      this._resize = new ResizeObserver(this._queue);
      this._resize.observe(this.closest('bm-dome-transition') || this);
      window.addEventListener('resize', this._queue, { passive: true });
      this.measure();
      if (document.fonts) {
        document.fonts.load('400 44px "BM Dome Editorial New"').then(this._queue).catch(() => {});
      }
    }

    measure() {
      const dome = this.closest('bm-dome-transition');
      const title = this.querySelector('.bm-dome-heading__title');
      const words = Array.from(this.querySelectorAll('.bm-dome-heading__word'));
      if (!dome || !title || !words.length) return;
      const width = dome.getBoundingClientRect().width;
      const depth = this.getBoundingClientRect().height;
      if (!width || !depth) return;
      const mobile = width < 768;
      const preferred = parseFloat(getComputedStyle(this).getPropertyValue('--bm-dome-type-size')) || (mobile ? 22 : 44);
      const rx = width * 0.43;
      const ry = depth * 0.85;
      const openAngle = (mobile ? 48 : 46) * Math.PI / 180;
      const closed = arcTable(rx, ry, openAngle * 0.78);
      const opened = arcTable(rx, ry, openAngle);
      title.style.fontSize = preferred + 'px';
      const initialWidths = words.map(word => word.offsetWidth);
      const naturalWidth = initialWidths.reduce((sum, n) => sum + n, 0);
      const minGap = mobile ? 7 : 14;
      const available = Math.max(1, closed.length - minGap * (words.length - 1));
      const fit = Math.min(1, available / Math.max(1, naturalWidth * 0.9));
      const fontSize = preferred * fit;

      // An unusually long merchant heading stays readable as ordinary text.
      if (fontSize < (mobile ? 14 : 20)) {
        this.dataset.layout = 'plain';
        return;
      }
      title.style.fontSize = fontSize.toFixed(2) + 'px';
      const widths = words.map(word => word.offsetWidth);
      const inset = Math.max(fontSize * 0.85, mobile ? 25 : 56);
      const rtl = getComputedStyle(title).direction === 'rtl';

      const place = (table, scale, state, compact = false) => {
        const total = widths.reduce((sum, n) => sum + n * scale, 0);
        const availableGap = words.length > 1 ? (table.length - total) / (words.length - 1) : 0;
        // Start as a centered phrase with normal word spacing, then fill the arc.
        const gap = compact ? Math.min(availableGap, fontSize * 0.2) : availableGap;
        let cursor = (table.length - total - gap * (words.length - 1)) / 2;
        words.forEach((word, i) => {
          const wordWidth = widths[i] * scale;
          let angle = atLength(table, cursor + wordWidth / 2);
          if (rtl) angle *= -1;
          const x = width / 2 + rx * Math.sin(angle);
          const y = inset + ry * (1 - Math.cos(angle));
          const rotation = Math.atan2(ry * Math.sin(angle), rx * Math.cos(angle)) * 180 / Math.PI;
          word.style.setProperty(`--word-${state}-x`, x.toFixed(3) + 'px');
          word.style.setProperty(`--word-${state}-y`, y.toFixed(3) + 'px');
          word.style.setProperty(`--word-${state}-angle`, rotation.toFixed(3) + 'deg');
          cursor += wordWidth + gap;
        });
      };
      place(closed, 0.9, 'closed', true);
      place(opened, 1, 'open');
      this.dataset.layout = 'arc';
    }

    disconnectedCallback() {
      if (!this._connected) return;
      this._connected = false;
      cancelAnimationFrame(this._frame);
      this._frame = 0;
      window.removeEventListener('resize', this._queue);
      this._resize.disconnect();
    }
  }

  customElements.define('bm-dome-heading', BmDomeHeading);
})();
